import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';

import type {
  CampaignListPagination,
  NetworkCampaign,
} from '@common/types/campaign';
import { CampaignList } from '@common/components/CampaignList';
import { CAMPAIGN_MAX_SIZE } from '@common/constants';

export type CampaignTabIndex = 0 | 1;

export type CampaignListHeaderContext = {
  selectedTab: CampaignTabIndex;
  isUserCampaignTab: boolean;
};

export type { CampaignListPagination };

/**
 * CampaignSection
 *
 * Renders the campaign section
 */
type CampaignSectionProps = {
  titleBlock: React.ReactNode;
  renderListHeader?: (context: CampaignListHeaderContext) => React.ReactNode;
  renderListFooter?: (context: CampaignListHeaderContext) => React.ReactNode;
  campaignList: NetworkCampaign[];
  userCampaignList: NetworkCampaign[];
  userIsLoggedIn: boolean;
  getCampaignHref: (campaign: NetworkCampaign) => string;
  openCampaignInNewTab?: boolean;
  maxCampaigns?: number;
  campaignListPagination?: CampaignListPagination;
  userCampaignListPagination?: CampaignListPagination;
  onChangeCampaignListPage?: (page: number) => void | Promise<void>;
  onChangeUserCampaignListPage?: (page: number) => void | Promise<void>;
  isCampaignListPaginationLoading?: boolean;
  isUserCampaignListPaginationLoading?: boolean;
  campaignListError?: string;
  userCampaignListError?: string;
  selectedTab: CampaignTabIndex;
  onSelectedTabChange: (tab: CampaignTabIndex) => void;
};

/**
 * Campaign section component that displays a list of campaigns
 *
 * @param props - The component props
 * @param props.titleBlock - The title block
 * @param props.renderListHeader - The render list header
 * @param props.renderListFooter - The render list footer
 * @param props.campaignList - The list of campaigns
 * @param props.userCampaignList - The list of user campaigns
 * @param props.userIsLoggedIn - Whether the user is logged in
 * @param props.getCampaignHref - The function to get the campaign href
 * @param props.openCampaignInNewTab - Whether to open the campaign in a new tab
 * @param props.maxCampaigns - The maximum number of campaigns to display
 * @param props.campaignListPagination - The pagination for the campaign list
 * @param props.userCampaignListPagination - The pagination for the user campaign list
 * @param props.onChangeCampaignListPage - The function to change the campaign list page
 * @param props.onChangeUserCampaignListPage - The function to change the user campaign list page
 * @param props.isCampaignListPaginationLoading - Whether the campaign list pagination is loading
 * @param props.isUserCampaignListPaginationLoading - Whether the user campaign list pagination is loading
 * @param props.campaignListError - The error message for the campaign list
 * @param props.userCampaignListError - The error message for the user campaign list
 * @param props.selectedTab - The selected tab
 * @param props.onSelectedTabChange - The function to change the selected tab
 * @returns The rendered campaign section
 */
export function CampaignSection({
  titleBlock,
  renderListHeader,
  renderListFooter,
  campaignList,
  userCampaignList,
  userIsLoggedIn,
  getCampaignHref,
  openCampaignInNewTab = false,
  maxCampaigns = CAMPAIGN_MAX_SIZE,
  campaignListPagination,
  userCampaignListPagination,
  onChangeCampaignListPage,
  onChangeUserCampaignListPage,
  isCampaignListPaginationLoading = false,
  isUserCampaignListPaginationLoading = false,
  campaignListError,
  userCampaignListError,
  selectedTab,
  onSelectedTabChange,
}: CampaignSectionProps) {
  const userCampaignTabLabel = 'エントリー済\nキャンペーン一覧';
  const campaignTabLabel = 'キャンペーン一覧';
  const isUserCampaignTab = userIsLoggedIn && selectedTab === 1;

  const campaignListPaginated =
    campaignListPagination !== undefined &&
    onChangeCampaignListPage !== undefined;
  const userCampaignListPaginated =
    userCampaignListPagination !== undefined &&
    onChangeUserCampaignListPage !== undefined;
  const activePagination = isUserCampaignTab
    ? userCampaignListPaginated
      ? userCampaignListPagination
      : undefined
    : campaignListPaginated
      ? campaignListPagination
      : undefined;
  const activeOnChangePage = isUserCampaignTab
    ? userCampaignListPaginated
      ? onChangeUserCampaignListPage
      : undefined
    : campaignListPaginated
      ? onChangeCampaignListPage
      : undefined;
  const activePaginationLoading = isUserCampaignTab
    ? isUserCampaignListPaginationLoading
    : isCampaignListPaginationLoading;
  const activeListError = isUserCampaignTab
    ? userCampaignListError
    : campaignListError;

  return (
    <Box id="campaign-section" sx={{ padding: '0px 16px 0px 16px' }}>
      {titleBlock}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={(theme) => ({
            marginBottom: '64px',
            [theme.breakpoints.down('md')]: {
              marginBottom: '48px',
            },
          })}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, value) => {
              onSelectedTabChange(value);
            }}
            indicatorColor="primaryColor"
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.colors.secondary}`,
              [theme.breakpoints.down('md')]: {
                width: '100%',
              },
            })}
          >
            <Tab
              sx={(theme) => ({
                marginX: '0px',
                paddingX: '12px',
                [theme.breakpoints.down('md')]: {
                  flexGrow: 1,
                  maxWidth: '100%',
                },
              })}
              key="first_tab_campaign"
              label={campaignTabLabel}
            />
            {userIsLoggedIn && (
              <Tab
                sx={(theme) => ({
                  marginX: '0px',
                  paddingX: '12px',
                  [theme.breakpoints.down('md')]: {
                    flexGrow: 1,
                    maxWidth: '100%',
                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                  },
                })}
                key="second_tab_userCampaign"
                label={userCampaignTabLabel}
              />
            )}
          </Tabs>
        </Box>

        {renderListHeader?.({
          isUserCampaignTab,
          selectedTab: selectedTab as CampaignTabIndex,
        })}

        <CampaignList
          campaigns={campaignList}
          userCampaigns={userCampaignList}
          showUserCampaigns={isUserCampaignTab}
          getCampaignHref={getCampaignHref}
          openCampaignInNewTab={openCampaignInNewTab}
          maxCampaigns={maxCampaigns}
          pagination={activePagination}
          onChangePage={activeOnChangePage}
          isPaginationLoading={activePaginationLoading}
          listError={activeListError}
        />

        {renderListFooter?.({
          isUserCampaignTab,
          selectedTab: selectedTab as CampaignTabIndex,
        })}
      </Box>
    </Box>
  );
}
